import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import ImageWithSkeletonLoader, { SKELETON_LOADER_SRC } from 'web-ui-kit/images/ImageWithSkeletonLoader';

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(n);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function useImageFallback(_ref) {
  var src = _ref.src; // Whether there is an active error for this image component

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      hasError = _useState2[0],
      setHasError = _useState2[1]; // Callback when there is an error on the image load


  var onError = useCallback(function () {
    setHasError(true);
  }, [setHasError]); // When the src changes, reset the error state

  useEffect(function () {
    setHasError(false);
  }, [src]);
  return {
    hasError: hasError,
    onError: onError
  };
}
/**
 * Renders an image using the ImageWithSkeletonLoader
 * If the image fails to load it falls back to the passed in component
 */


function ImageWithFallback(_ref) {
  var _ref$alt = _ref.alt,
      alt = _ref$alt === void 0 ? '' : _ref$alt,
      _ref$bucketName = _ref.bucketName,
      bucketName = _ref$bucketName === void 0 ? '' : _ref$bucketName,
      _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className,
      _ref$fallbackComponen = _ref.fallbackComponent,
      fallbackComponent = _ref$fallbackComponen === void 0 ? {} : _ref$fallbackComponen,
      _ref$onClick = _ref.onClick,
      onClick = _ref$onClick === void 0 ? function () {} : _ref$onClick,
      _ref$sizes = _ref.sizes,
      sizes = _ref$sizes === void 0 ? '' : _ref$sizes,
      _ref$src = _ref.src,
      src = _ref$src === void 0 ? SKELETON_LOADER_SRC : _ref$src,
      _ref$useImgix = _ref.useImgix,
      useImgix = _ref$useImgix === void 0 ? true : _ref$useImgix,
      _ref$useLoadingHook = _ref.useLoadingHook,
      useLoadingHook = _ref$useLoadingHook === void 0 ? undefined : _ref$useLoadingHook,
      _ref$useFallbackHook = _ref.useFallbackHook,
      useFallbackHook = _ref$useFallbackHook === void 0 ? useImageFallback : _ref$useFallbackHook,
      _ref$width = _ref.width,
      width = _ref$width === void 0 ? undefined : _ref$width,
      _ref$height = _ref.height,
      height = _ref$height === void 0 ? undefined : _ref$height;

  var _useFallbackHook = useFallbackHook({
    src: src
  }),
      hasError = _useFallbackHook.hasError,
      onError = _useFallbackHook.onError;

  return !hasError ?
  /*#__PURE__*/
  React.createElement(ImageWithSkeletonLoader, {
    alt: alt,
    bucketName: bucketName,
    className: className,
    onClick: onClick,
    onError: onError,
    sizes: sizes,
    src: src,
    useImgix: useImgix,
    useLoadingHook: useLoadingHook,
    width: width,
    height: height
  }) : fallbackComponent;
}

ImageWithFallback.propTypes = {
  /* The image alt text */
  alt: PropTypes.string,

  /* The imgix bucketName if using imgix */
  bucketName: PropTypes.string,

  /* Styled Components classname */
  className: PropTypes.string,

  /* The component to render when the image fails to load */
  fallbackComponent: PropTypes.object,

  /* Sizes prop for imgix */
  sizes: PropTypes.string,

  /* The image src */
  src: PropTypes.string,

  /* Determines whether the image should be rendered using imgix */
  useImgix: PropTypes.bool,

  /* Optional hook for controlling loading behavior, defaults to integrated */
  useLoadingHook: PropTypes.func,

  /* Optional hook for controlling fallback behavior, defaults to integrated */
  useFallbackHook: PropTypes.func,

  /* Optional static width for image */
  width: PropTypes.number,

  /* Optional static height for image */
  height: PropTypes.number
};
export default ImageWithFallback;